import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function LocationsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [budget, setBudget] = useState("");
  const [editing, setEditing] = useState(null); // location being edited
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setAddress("");
    setBudget("");
    setOpen(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setName(loc.name);
    setAddress(loc.address || "");
    setBudget(loc.monthly_budget != null ? String(loc.monthly_budget) : "");
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    const body = {
      name: name.trim(),
      address: address.trim(),
      monthly_budget: budget === "" ? null : Number(budget),
    };
    try {
      if (editing) {
        await api.patch(`/locations/${editing.id}`, body);
        toast.success("Location updated");
      } else {
        await api.post("/locations", body);
        toast.success("Location added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Locations</h1>
            <p className="text-sm text-gray-600 mt-2">
              Add or remove locations. Each location user reports from one of these.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] h-11"
              data-testid="add-location-btn"
              onClick={openCreate}
            >
              <Plus size={16} className="mr-1" /> New location
            </Button>
            <DialogContent data-testid="add-location-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl">
                  {editing ? "Edit location" : "New location"}
                </DialogTitle>
                <DialogDescription>
                  {editing ? "Update name, address, or monthly maintenance budget." : "Add a site where issues will be reported from."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                  <Input
                    data-testid="location-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Downtown Store"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Address (optional)</Label>
                  <Input
                    data-testid="location-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Monthly maintenance budget (optional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      data-testid="location-budget"
                      type="number"
                      inputMode="decimal"
                      step="1"
                      min="0"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      className="pl-7"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500">Used on the Cost Reports page to flag overspend.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="save-location"
                  className="bg-[#0055FF] hover:bg-[#0044CC]"
                  disabled={saving}
                  onClick={save}
                >
                  {editing ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
            <p className="font-display text-xl font-bold text-gray-800">No locations yet</p>
            <p className="text-sm text-gray-600 mt-1">Add your 13 locations to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="locations-grid">
            {items.map((l) => (
              <div
                key={l.id}
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                data-testid={`location-card-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => openEdit(l)}
                    data-testid={`edit-location-${l.id}`}
                    className="flex items-center gap-3 text-left flex-1 min-w-0"
                  >
                    <div className="w-10 h-10 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center shrink-0">
                      <MapPin size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-display font-semibold text-lg tracking-tight truncate">{l.name}</p>
                      {l.address && <p className="text-xs text-gray-500 mt-0.5 truncate">{l.address}</p>}
                      {l.monthly_budget != null && (
                        <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                          Budget: ${Number(l.monthly_budget).toLocaleString()}/mo
                        </p>
                      )}
                    </div>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(l.id)}
                    data-testid={`delete-location-${l.id}`}
                    aria-label="Delete"
                    className="text-gray-400 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
